import React, { useState } from "react"
import { faEye, faTrash, faDownload, faPlus } from "@fortawesome/free-solid-svg-icons"
import HeaderFilters from "components/Shared/HeaderFilters/HeaderFilters"
import Button from "components/Shared/Button/Button"
import BaseTable, { AutoResizer, ColumnShape } from "react-base-table"
import { Form, Modal, TextArea, Pagination, Loading } from "@wfp/ui"
import { useDeleteReportMutation, useSaveReportMutation } from "services/marketprices/reports"
import { useNavigate } from "react-router-dom"
import { useGetGorpReportsQuery } from "services/gorp"
import { GET_ITEM_LIMIT } from "constants/constants"
import { GetGorpReport } from "interfaces/dataInterfaces"
import { formatDate } from "utils/helpers"
import DeleteConfirmationModal from "components/Shared/ConfirmationModal/DeleteConfirmationModal"

function Reports() {
  const navigate = useNavigate()

  const [pageNumber, setPageNumber] = useState(1)
  const [getItemLimit, setGetItemLimit] = useState(GET_ITEM_LIMIT)

  const { data: gorpReports, isLoading: isFetching } = useGetGorpReportsQuery({
    skip: pageNumber === 1 ? 0 : (pageNumber - 1) * getItemLimit,
    top: getItemLimit,
  })

  const [saveReport, { isLoading }] = useSaveReportMutation()
  const [deleteReport, { isLoading: isDeleting }] = useDeleteReportMutation()

  const [showModal, setShowModal] = useState(false)
  const [notes, setNotes] = useState("")
  const [actionItem, setActionItem] = useState<{
    item: GetGorpReport
    action: "delete"
  } | null>(null)

  const handleModalClose = () => {
    setShowModal(false)
  }

  const handleDelete = async (id: number) => {
    await deleteReport(id)
    setActionItem(null)
  }

  const handleReportSave = async () => {
    if (!notes) return
    const res = await saveReport({
      mappingSchemaId: 0,
      reportTypeId: 3,
      adm0Code: 0,
      reportComment: notes,
    }).unwrap()
    navigate(`/Gorp/GorpReports/Excel/${res?.reportID}`)
  }

  const columns: Array<ColumnShape<GetGorpReport>> = [
    {
      key: "reportId",
      dataKey: "reportId",
      title: "ID",
      width: 0,
      flexGrow: 1,
    },
    {
      key: "reportCreateDate",
      title: "Create Date",
      width: 0,
      flexGrow: 1,
      cellRenderer: ({ rowData }) => formatDate(rowData.reportCreateDate || "", "DD-MMMM-YYYY HH:mm"),
    },
    {
      key: "uploadedByUserName",
      dataKey: "uploadedByUserName",
      title: "Created By",
      width: 0,
      flexGrow: 1,
    },
    {
      key: "action",
      title: "Action",
      width: 0,
      flexGrow: 2,
      cellRenderer: ({ rowData }) => (
        <>
          <Button
            action={() => navigate(`/Gorp/GorpViewData?reportId=${rowData.reportId}`)}
            label="View"
            className="btn-full blue action-btn"
            icon={faEye}
          />

          <Button
            action={() =>
              setActionItem({
                item: rowData,
                action: "delete",
              })
            }
            label="Delete"
            className="btn-full danger action-btn"
            style={{ marginLeft: "10px" }}
            icon={faTrash}
          />
        </>
      ),
    },
  ]

  return (
    <>
      <Loading active={isLoading || isDeleting || isFetching} />
      <HeaderFilters>
        <div className="d-flex" style={{ width: "68%" }} />
        <Button
          action={() => window.open("/gorp-template.xlsx")}
          label="Download Template"
          className="btn-full blue"
          icon={faDownload}
        />
        <Button action={() => setShowModal(true)} label="Add New Report" className="btn-full green" icon={faPlus} />
      </HeaderFilters>
      <div
        style={{
          height: "100%",
          minHeight: "60vh",
          marginTop: "2%",
        }}
      >
        <AutoResizer>
          {({ width, height }) => (
            <BaseTable
              data={gorpReports || []}
              width={width}
              height={height}
              rowKey="reportId"
              className="custom-table"
              rowHeight={38}
              headerHeight={45}
              columns={columns}
              emptyRenderer={() => <p>No records to display</p>}
            />
          )}
        </AutoResizer>
      </div>

      <Modal
        open={showModal}
        onRequestSubmit={handleReportSave}
        onRequestClose={handleModalClose}
        modalHeading="Add New Report"
        primaryButtonText="Start"
        secondaryButtonText="Cancel"
      >
        <Form>
          <TextArea labelText="Notes" name="note" onChange={e => setNotes(e.target.value)} />
        </Form>
      </Modal>

      <DeleteConfirmationModal
        open={actionItem?.action === "delete"}
        onRequestSubmit={() => handleDelete(actionItem!.item.reportId)}
        onRequestClose={() => setActionItem(null)}
      >
        Are you sure you want to delete the selected record?
      </DeleteConfirmationModal>

      <Pagination
        totalItems={gorpReports?.length ?? 0}
        pageSize={getItemLimit}
        pageSizes={[10, 20, 30]}
        page={pageNumber}
        onChange={(event: any): void => {
          setPageNumber((event as any).page)
          setGetItemLimit((event as any).pageSize)
        }}
      />
    </>
  )
}

export default Reports
